
import { useEffect, useState } from 'react'
import { Icon, type IconName } from '@/components/ui/Icon'
import { btn, cx, iconBtn, notice } from '@/lib/ui'
import { PRESET_CARDS, type PresetCard } from '@/lib/presets/specs'
import { useModal } from '@/lib/use-modal'

/** A glyph per preset; anything new falls back to the sparkle. */
const PRESET_ICONS: Record<string, IconName> = {
  podcast: 'headset',
  'video-podcast': 'camera',
  'single-camera': 'broadcast',
  'live-stream': 'cable',
}

/**
 * "New project": start blank, or drop in a finished rig. Presets are static
 * data, so the cards render without a request; only choosing one talks to
 * the server.
 */
type Availability = { available: boolean; missing: string[] }

export function PresetPicker({
  onBlank,
  onPreset,
  onClose,
  busyKey,
  error,
  apiBase,
}: {
  onBlank: () => void
  onPreset: (preset: PresetCard) => void
  onClose: () => void
  /** The preset being created, while the request is in flight. */
  busyKey: string | null
  error: string | null
  apiBase: string
}) {
  // Focus in, Tab trapped, focus back to "New project" on close.
  const dialogRef = useModal<HTMLDivElement>({ onClose: () => !busyKey && onClose() })

  // The cards render immediately from static data; whether each preset's gear
  // is in this workspace's catalogue needs the server, so it arrives after.
  // Until it does nothing is disabled, because the common case is that they
  // all work and a flicker of disabled cards would be worse than the rare
  // wasted click.
  const [availability, setAvailability] = useState<Record<string, Availability>>({})
  useEffect(() => {
    let live = true
    fetch(`${apiBase}/projects/presets`)
      .then((response) => (response.ok ? response.json() : null))
      .then((cards: (PresetCard & Availability)[] | null) => {
        if (!live || !cards) return
        setAvailability(
          Object.fromEntries(cards.map((card) => [card.key, { available: card.available, missing: card.missing }])),
        )
      })
      .catch(() => {})
    return () => {
      live = false
    }
  }, [apiBase])

  return (
    <div
      className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-[var(--scrim)] p-4 backdrop-blur-[2px] sm:p-8 sm:pt-[12vh]"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busyKey) onClose()
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && !busyKey) onClose()
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="preset-picker-title"
        className="panel w-full max-w-2xl rounded-[var(--radius-card)] p-5 shadow-[var(--shadow-float)] sm:p-6"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 id="preset-picker-title" className="text-lg font-semibold tracking-tight">
              New project
            </h2>
            <p className="mt-1 text-sm text-muted">
              Start with an empty canvas, or drop in a finished rig and change what you like.
            </p>
          </div>
          <button type="button" onClick={onClose} disabled={busyKey !== null} className={iconBtn} aria-label="Close">
            <Icon name="close" className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <button
            type="button"
            onClick={onBlank}
            disabled={busyKey !== null}
            className={cx(
              'group flex items-start gap-3 rounded-2xl border border-[var(--border)] bg-surface/60 p-4 text-left transition-colors hover:bg-hover',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]',
            )}
          >
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[var(--ink)] text-on-ink">
              <Icon name="plus" className="h-4 w-4" strokeWidth={2} />
            </span>
            <span>
              <span className="block text-sm font-semibold">Blank canvas</span>
              <span className="mt-0.5 block text-xs text-muted">Name it and start placing equipment.</span>
            </span>
          </button>

          {PRESET_CARDS.map((preset) => {
            const busy = busyKey === preset.key
            const state = availability[preset.key]
            const unavailable = state ? !state.available : false
            return (
              <button
                key={preset.key}
                type="button"
                onClick={() => onPreset(preset)}
                disabled={busyKey !== null || unavailable}
                title={
                  unavailable
                    ? `Not available yet — this workspace's equipment list is missing ${state!.missing.join(', ')}`
                    : undefined
                }
                data-preset={preset.key}
                className={cx(
                  'group flex items-start gap-3 rounded-2xl border border-[var(--border)] bg-surface/60 p-4 text-left transition-colors hover:bg-hover disabled:opacity-60',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]',
                  busy && 'opacity-100',
                  unavailable && 'opacity-55',
                )}
              >
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-surface-raised text-foreground/80 shadow-sm">
                  <Icon name={PRESET_ICONS[preset.key] ?? 'sparkle'} className="h-4 w-4" />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-semibold">{preset.name}</span>
                  <span className="mt-0.5 block text-xs text-muted">{preset.tagline}</span>
                  <span
                    className={cx(
                      'mt-2 block text-[11px] font-medium',
                      unavailable ? 'text-warning' : 'text-foreground/70',
                    )}
                  >
                    {busy
                      ? 'Creating…'
                      : unavailable
                        ? 'Not available yet'
                        : `${preset.devices} devices · ${preset.cables} cables`}
                  </span>
                </span>
              </button>
            )
          })}
        </div>

        {error && <p role="alert" className={cx('mt-4', notice.error)}>{error}</p>}

        <p className="mt-4 text-xs text-muted">
          Presets are built from the shared catalogue. Each one is a normal project once created: rename it, add
          gear, or delete it.
        </p>
        <div className="mt-4 flex justify-end">
          <button type="button" onClick={onClose} disabled={busyKey !== null} className={cx(btn.base, btn.secondary)}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}
